import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

const TABLE_LIMIT = 50;

const WALLET_TYPES = [
  { value: "all", label: "All types" },
  { value: "withdraw_request", label: "withdraw_request" },
  { value: "withdraw_paid", label: "withdraw_paid" },
  { value: "withdraw_revert", label: "withdraw_revert" },
  { value: "call_earning", label: "call_earning" },
  { value: "gift_earning", label: "gift_earning" },
  { value: "token_to_coins", label: "token_to_coins" },
];

const WITHDRAW_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "pending" },
  { value: "under_review", label: "under_review" },
  { value: "approved", label: "approved" },
  { value: "paid", label: "paid" },
  { value: "rejected", label: "rejected" },
  { value: "cancelled", label: "cancelled" },
];

const REQUEST_STATUSES = new Set(["pending", "under_review", "approved"]);

function dayBoundsMs(isoDate, endOfDay) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}

function formatMs(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function formatTs(value) {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return "—";
    }
  }
  return String(value);
}

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Prefer explicit USD/amount fields used across providers */
function topupPaidMoney(data) {
  const d = data || {};
  const candidates = [
    d.amountUsd,
    d.amountUSD,
    d.amount,
    d.price,
    d.totalUsd,
    d.usd,
    d.totalAmount,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function isTopupPaidSuccess(data) {
  const st = normalize(data?.status);
  return ["success", "paid", "completed", "succeeded"].includes(st);
}

export default function FinancePage() {
  const { t } = useTranslation();
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultFromIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const [fromDate, setFromDate] = useState(defaultFromIso);
  const [toDate, setToDate] = useState(todayIso);
  const [walletType, setWalletType] = useState("all");
  const [withdrawStatus, setWithdrawStatus] = useState("all");

  const [walletRows, setWalletRows] = useState([]);
  const [topupRows, setTopupRows] = useState([]);
  const [withdrawRows, setWithdrawRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fromMs = dayBoundsMs(fromDate, false);
  const toMs = dayBoundsMs(toDate, true);

  const loadData = useCallback(async () => {
    if (fromMs == null || toMs == null || fromMs > toMs) {
      setError("Invalid date range.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const fromTs = Timestamp.fromMillis(fromMs);
      const toTs = Timestamp.fromMillis(toMs);

      const txConstraints = [
        where("createdAtMs", ">=", fromMs),
        where("createdAtMs", "<=", toMs),
      ];
      if (walletType !== "all") {
        txConstraints.push(where("type", "==", walletType));
      }
      txConstraints.push(orderBy("createdAtMs", "desc"));

      const wdConstraints = [
        where("createdAtMs", ">=", fromMs),
        where("createdAtMs", "<=", toMs),
      ];
      if (withdrawStatus !== "all") {
        wdConstraints.push(where("status", "==", withdrawStatus));
      }
      wdConstraints.push(orderBy("createdAtMs", "desc"));

      const qTx = query(collection(db, "walletTransactions"), ...txConstraints);
      const qWd = query(collection(db, "withdrawals"), ...wdConstraints);

      const qTp = query(
        collection(db, "topups"),
        where("createdAt", ">=", fromTs),
        where("createdAt", "<=", toTs),
        orderBy("createdAt", "desc")
      );

      async function loadTopupsSnap() {
        try {
          return await getDocs(qTp);
        } catch (tpErr) {
          console.warn(
            "FinancePage topups indexed query failed, using scan:",
            tpErr
          );
          return getDocs(collection(db, "topups"));
        }
      }

      const [txSnap, wdSnap, tpSnap] = await Promise.all([
        getDocs(qTx),
        getDocs(qWd),
        loadTopupsSnap(),
      ]);

      const wallets = [];
      txSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const createdMs =
          toNumber(d.createdAtMs, 0) ||
          (typeof d.createdAt?.toMillis === "function"
            ? d.createdAt.toMillis()
            : 0);
        let coinsCol = null;
        if (d.amountCoins != null) coinsCol = toNumber(d.amountCoins, 0);
        else if (d.coinsReceived != null) coinsCol = toNumber(d.coinsReceived, 0);
        else if (d.amount != null) coinsCol = toNumber(d.amount, 0);
        wallets.push({
          id: docSnap.id,
          uid: String(d.uid || ""),
          type: String(d.type || ""),
          amountTokens: toNumber(
            d.amountTokens ?? d.tokensUsed ?? d.tokensDeducted,
            0
          ),
          amountCoins: coinsCol,
          direction: String(d.direction || ""),
          relatedId:
            d.relatedId != null && d.relatedId !== ""
              ? String(d.relatedId)
              : "—",
          createdAtMs: createdMs,
          createdAt: d.createdAt,
        });
      });

      const withdraws = [];
      wdSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        withdraws.push({
          id: docSnap.id,
          uid: String(d.uid || ""),
          amountTokens: toNumber(d.amountTokens, 0),
          requestedUsdPreview: toNumber(d.requestedUsdPreview, 0),
          status: String(d.status || ""),
          method: String(d.method || ""),
          createdAtMs: toNumber(d.createdAtMs, 0),
          createdAt: d.createdAt,
        });
      });

      const topups = [];
      tpSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const ms =
          d.createdAt?.toMillis?.() ?? toNumber(d.createdAtMs, 0);
        if (ms < fromMs || ms > toMs) return;
        topups.push({
          id: docSnap.id,
          uid: String(d.uid || ""),
          provider: String(d.provider || d.type || "—"),
          amount: topupPaidMoney(d),
          currency: String(d.currency || "USD"),
          coins: toNumber(d.coins, 0),
          status: String(d.status || ""),
          createdAt: d.createdAt,
          createdAtMs: ms,
        });
      });

      topups.sort((a, b) => b.createdAtMs - a.createdAtMs);

      setWalletRows(wallets);
      setWithdrawRows(withdraws);
      setTopupRows(topups);
    } catch (e) {
      console.error("FinancePage load error:", e);
      setError(
        e?.message ||
          "Failed to load finance data. Check Firestore indexes for walletTransactions, withdrawals, and topups."
      );
      setWalletRows([]);
      setWithdrawRows([]);
      setTopupRows([]);
    } finally {
      setLoading(false);
    }
  }, [fromMs, toMs, walletType, withdrawStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(() => {
    let totalTopupsPaid = 0;
    let totalCoinsPurchased = 0;
    for (const t of topupRows) {
      if (isTopupPaidSuccess({ status: t.status })) {
        totalTopupsPaid += t.amount;
        totalCoinsPurchased += t.coins;
      }
    }

    let totalRequestTokens = 0;
    let totalPaidTokens = 0;
    let totalPaidUsd = 0;

    for (const w of withdrawRows) {
      const st = normalize(w.status);
      if (REQUEST_STATUSES.has(st)) {
        totalRequestTokens += w.amountTokens;
      }
      if (st === "paid") {
        totalPaidTokens += w.amountTokens;
        totalPaidUsd += w.requestedUsdPreview;
      }
    }

    let platformUsd = totalTopupsPaid - totalPaidUsd;
    const tokenNet = totalCoinsPurchased - totalPaidTokens;

    return {
      totalTopupsPaid,
      totalCoinsPurchased,
      totalRequestTokens,
      totalPaidTokens,
      totalPaidUsd,
      platformUsd,
      tokenNet,
    };
  }, [topupRows, withdrawRows]);

  const nf = useMemo(() => new Intl.NumberFormat(), []);
  const usd = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }),
    []
  );

  const walletTable = useMemo(
    () => walletRows.slice(0, TABLE_LIMIT),
    [walletRows]
  );
  const topupTable = useMemo(
    () => topupRows.slice(0, TABLE_LIMIT),
    [topupRows]
  );
  const withdrawTable = useMemo(
    () => withdrawRows.slice(0, TABLE_LIMIT),
    [withdrawRows]
  );

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.finance.title", { defaultValue: "Finance" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.finance.subtitle", { defaultValue: "Read-only overview of wallet ledger, topups, and withdrawals for the selected period. USD totals depend on fields present on topup docs." })}
        </p>
      </header>

      <div className="admin1-finance__toolbar">
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("common.from", { defaultValue: "From" })}</span>
          <input
            type="date"
            className="admin1-users__input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("common.to", { defaultValue: "To" })}</span>
          <input
            type="date"
            className="admin1-users__input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("admin.finance.wallet_transaction_type", { defaultValue: "Wallet transaction type" })}</span>
          <select
            className="admin1-users__select"
            value={walletType}
            onChange={(e) => setWalletType(e.target.value)}
          >
            {WALLET_TYPES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("admin.withdrawals.status", { defaultValue: "Withdrawal status" })}</span>
          <select
            className="admin1-users__select"
            value={withdrawStatus}
            onChange={(e) => setWithdrawStatus(e.target.value)}
          >
            {WITHDRAW_STATUS_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="admin1-state-card admin1-finance__error">{error}</div>
      ) : null}

      {loading ? (
        <div className="admin1-state-card">{t("admin.finance.loading_data", { defaultValue: "Loading finance data..." })}</div>
      ) : null}

      {!loading && !error ? (
        <>
          <section className="admin1-finance__cards">
            <article className="admin1-finance__card">
              <div className="admin1-finance__card-label">
                {t("admin.finance.total_topups_paid_usd_est", { defaultValue: "Total topups paid (USD est.)" })}
              </div>
              <div className="admin1-finance__card-value">
                {usd.format(summary.totalTopupsPaid)}
              </div>
              <div className="admin1-finance__card-note">
                {t("admin.finance.total_topups_paid_note", { defaultValue: "Sum of amount/USD fields on successful topups in range" })}
              </div>
            </article>
            <article className="admin1-finance__card">
              <div className="admin1-finance__card-label">
                {t("admin.finance.total_coins_purchased", { defaultValue: "Total coins purchased" })}
              </div>
              <div className="admin1-finance__card-value">
                {nf.format(summary.totalCoinsPurchased)}
              </div>
              <div className="admin1-finance__card-note">
                {t("admin.finance.successful_topups_sum_coins", { defaultValue: "Successful topups: sum of coins" })}
              </div>
            </article>
            <article className="admin1-finance__card">
              <div className="admin1-finance__card-label">
                {t("admin.finance.withdraw_requests_tokens", { defaultValue: "Withdraw requests (tokens)" })}
              </div>
              <div className="admin1-finance__card-value">
                {nf.format(summary.totalRequestTokens)}
              </div>
              <div className="admin1-finance__card-note">
                {t("admin.finance.withdraw_requests_note", { defaultValue: "pending + under_review + approved in filtered withdrawals" })}
              </div>
            </article>
            <article className="admin1-finance__card">
              <div className="admin1-finance__card-label">
                {t("admin.finance.withdraw_paid_tokens", { defaultValue: "Withdraw paid (tokens)" })}
              </div>
              <div className="admin1-finance__card-value">
                {nf.format(summary.totalPaidTokens)}
              </div>
              <div className="admin1-finance__card-note">
                {t("admin.finance.withdraw_paid_note", { defaultValue: "Status paid in filtered withdrawals" })}
              </div>
            </article>
            <article className="admin1-finance__card admin1-finance__card--wide">
              <div className="admin1-finance__card-label">
                {t("admin.finance.platform_estimated_balance", { defaultValue: "Platform estimated balance" })}
              </div>
              <div className="admin1-finance__card-value">
                {usd.format(summary.platformUsd)}
              </div>
              <div className="admin1-finance__card-note">
                {t("admin.finance.platform_balance_note_before_net", { defaultValue: "Topups paid (USD est.) minus paid withdrawal USD preview in period. Token net (coins - paid tokens):" })}{" "}
                <strong>{nf.format(summary.tokenNet)}</strong> {t("admin.finance.informative_suffix", { defaultValue: "(informative)." })}
              </div>
            </article>
          </section>

          <section className="admin1-finance__section">
            <h2 className="admin1-finance__section-title">
                {t("admin.finance.recent_wallet_transactions", { defaultValue: "Recent wallet transactions (max {{limit}})", limit: TABLE_LIMIT })}
            </h2>
            {walletTable.length === 0 ? (
              <div className="admin1-state-card">{t("common.no_rows", { defaultValue: "No rows." })}</div>
            ) : (
              <div className="admin1-users__table-wrap">
                <table className="admin1-users__table admin1-finance__table">
                  <thead>
                    <tr>
                      <th>{t("admin.fields.uid", { defaultValue: "uid" })}</th>
                      <th>{t("common.type", { defaultValue: "type" })}</th>
                      <th>{t("admin.finance.amount_tokens", { defaultValue: "amountTokens" })}</th>
                      <th>{t("admin.finance.amount_coins", { defaultValue: "amountCoins" })}</th>
                      <th>{t("admin.finance.direction", { defaultValue: "direction" })}</th>
                      <th>{t("admin.fields.related_id", { defaultValue: "relatedId" })}</th>
                      <th>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walletTable.map((r) => (
                      <tr key={r.id}>
                        <td className="admin1-reports__mono">{r.uid}</td>
                        <td>{r.type}</td>
                        <td>{nf.format(r.amountTokens)}</td>
                        <td>
                          {r.amountCoins != null
                            ? nf.format(r.amountCoins)
                            : "—"}
                        </td>
                        <td>{r.direction}</td>
                        <td className="admin1-reports__mono">{r.relatedId}</td>
                        <td>
                          {r.createdAtMs
                            ? formatMs(r.createdAtMs)
                            : formatTs(r.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="admin1-finance__section">
            <h2 className="admin1-finance__section-title">
              Recent topups (max {TABLE_LIMIT})
            </h2>
            {topupTable.length === 0 ? (
              <div className="admin1-state-card">{t("common.no_rows", { defaultValue: "No rows." })}</div>
            ) : (
              <div className="admin1-users__table-wrap">
                <table className="admin1-users__table admin1-finance__table">
                  <thead>
                    <tr>
                      <th>{t("admin.fields.uid", { defaultValue: "uid" })}</th>
                      <th>{t("admin.finance.provider", { defaultValue: "provider" })}</th>
                      <th>{t("admin.finance.amount", { defaultValue: "amount" })}</th>
                      <th>{t("admin.finance.currency", { defaultValue: "currency" })}</th>
                      <th>{t("admin.finance.coins", { defaultValue: "coins" })}</th>
                      <th>{t("common.status", { defaultValue: "status" })}</th>
                      <th>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topupTable.map((r) => (
                      <tr key={r.id}>
                        <td className="admin1-reports__mono">{r.uid}</td>
                        <td>{r.provider}</td>
                        <td>{r.amount ? nf.format(r.amount) : "—"}</td>
                        <td>{r.currency}</td>
                        <td>{nf.format(r.coins)}</td>
                        <td>{r.status}</td>
                        <td>
                          {r.createdAtMs
                            ? formatMs(r.createdAtMs)
                            : formatTs(r.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="admin1-finance__section">
            <h2 className="admin1-finance__section-title">
              Recent withdrawals (max {TABLE_LIMIT})
            </h2>
            {withdrawTable.length === 0 ? (
              <div className="admin1-state-card">{t("common.no_rows", { defaultValue: "No rows." })}</div>
            ) : (
              <div className="admin1-users__table-wrap">
                <table className="admin1-users__table admin1-finance__table">
                  <thead>
                    <tr>
                      <th>{t("admin.fields.uid", { defaultValue: "uid" })}</th>
                      <th>{t("admin.finance.amount_tokens", { defaultValue: "amountTokens" })}</th>
                      <th>{t("admin.finance.requested_usd_preview", { defaultValue: "requestedUsdPreview" })}</th>
                      <th>{t("common.status", { defaultValue: "status" })}</th>
                      <th>{t("admin.withdrawals.method", { defaultValue: "method" })}</th>
                      <th>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawTable.map((r) => (
                      <tr key={r.id}>
                        <td className="admin1-reports__mono">{r.uid}</td>
                        <td>{nf.format(r.amountTokens)}</td>
                        <td>{nf.format(r.requestedUsdPreview)}</td>
                        <td>{r.status}</td>
                        <td>{r.method}</td>
                        <td>
                          {r.createdAtMs
                            ? formatMs(r.createdAtMs)
                            : formatTs(r.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
